import type { ChangeEvent } from 'react';
import { useMemo } from 'react';

import { useHelpRequestStore, useTableStore } from '@stores';
import { useLookupTableDivisionsAndDistricts } from '@api/services/lookup/hooks';
import { useNavigate } from '@tanstack/react-router';
import useGetRegulatorHelpRequests from '@api/services/dashboard/hooks/useGetRegulatorHelpRequests';
import { LookupDistrict } from '@src/api/services/lookup/types';
import { HelpRequestStatuses } from '@src/utils/enums';

import Filters from '@pages/common/components/filters/Filters';
import { createFilterSelectionActions } from '@pages/common/components/filters/filters.actions';
import { mapValueCountsToItems } from '@pages/common/components/filters/filters.mappers';
import { getStateNameFromCode } from '@src/utils/helpers';

type FilterType = 'category' | 'county' | 'district' | 'state' | 'status';

type DashboardSearchInput = {
  districtCodes?: string[];
  statuses?: string[];
  counties?: string[];
  states?: string[];
  helpCategories?: string[];
  pageNumber?: number;
};

type UpdateDashboardUrlInput = {
  nextDistrictCodes?: string[];
  nextStatuses?: string[];
  nextCounties?: string[];
  nextStates?: string[];
  nextHelpCategories?: string[];
  nextPageNumber?: number;
};

const categoryDisplayToCode: Record<string, string> = {
  'Request Questions': 'UnsureQues',
  'Impact Calculation': 'ImpactCalc',
  'Impact Determination': 'UnsureImp',
  'Aquatic Resource Classification': 'ClassAR',
  'Verify Response': 'VerifyQues',
  'Supporting Documents': 'UnsureSuppDoc',
  'Permit Type': 'UnsurePerm',
  Other: 'None',
};

const categoryCodeToDisplay: Record<string, string> = {
  UnsureQues: 'Request Questions',
  ImpactCalc: 'Impact Calculation',
  UnsureImp: 'Impact Determination',
  ClassAR: 'Aquatic Resource Classification',
  VerifyQues: 'Verify Response',
  UnsureSuppDoc: 'Supporting Documents',
  UnsurePerm: 'Permit Type',
  None: 'Other',
};

const mapCategoryNameToCode = (categoryName: string) => categoryDisplayToCode[categoryName] ?? categoryName;
const mapCategoryCodeToDisplay = (categoryCode: string) => categoryCodeToDisplay[categoryCode] ?? categoryCode;

const buildDashboardSearch = ({
  districtCodes = [],
  statuses = [],
  states = [],
  counties = [],
  helpCategories = [],
  pageNumber = 1,
}: DashboardSearchInput) => ({
  district: districtCodes.length ? districtCodes.join(',') : undefined,
  status: statuses.length ? statuses.join(',') : undefined,
  state: states.length ? states.join(',') : undefined,
  county: counties.length ? counties.join(',') : undefined,
  helpCategories: helpCategories.length ? helpCategories.join(',') : undefined,
  page: pageNumber > 1 ? pageNumber : undefined,
});

const HelpRequestFilters = ({ tableId, view = 'all' }: { tableId: string; view: 'unclaimed' | 'claimed' | 'all' }) => {
  const navigate = useNavigate();
  const {
    districts,
    statuses,
    states,
    counties,
    setDistricts,
    setHelpCategories,
    setStatuses,
    setStates,
    setCounties,
    helpCategories,
  } = useHelpRequestStore();
  const { getPageNumber, setPageNumber } = useTableStore();
  const pageNumber = getPageNumber(tableId);

  const queryStatuses = useMemo(() => {
    if (view === 'unclaimed') {
      return `${HelpRequestStatuses.Open}`;
    }

    return statuses.length ? statuses.join(',') : undefined;
  }, [statuses, view]);

  const queryClaimed = view === 'claimed';

  const payload = useMemo(
    () => ({
      page: pageNumber ?? 1,
      size: 10,
      column: 'createdDate' as const,
      dir: 'desc' as const,
      districts: districts.length ? districts.join(',') : undefined,
      states: states.length ? states.join(',') : undefined,
      counties: counties.length ? counties.join(',') : undefined,
      statuses: queryStatuses,
      helpCategories: helpCategories.length ? helpCategories.join(',') : undefined,
      claimed: queryClaimed,
    }),
    [counties, districts, helpCategories, pageNumber, queryClaimed, queryStatuses, states]
  );

  const { data, isLoading } = useGetRegulatorHelpRequests(payload);
  const districtsLookup = useLookupTableDivisionsAndDistricts();

  const mapDistrictNameToCode = (districtName: string) => {
    const normalized = districtName.trim().toLowerCase();
    const district = districtsLookup.find((d: LookupDistrict) => (d.orgName ?? '').trim().toLowerCase() === normalized);
    return district?.shortName ?? '';
  };

  const updateDashboardUrl = ({
    nextDistrictCodes = districts,
    nextStates = states,
    nextCounties = counties,
    nextStatuses = statuses,
    nextHelpCategories = helpCategories,
    nextPageNumber = pageNumber,
  }: UpdateDashboardUrlInput = {}) => {
    navigate({
      to: '/dashboard',
      search: buildDashboardSearch({
        districtCodes: nextDistrictCodes,
        states: nextStates,
        counties: nextCounties,
        statuses: nextStatuses,
        helpCategories: nextHelpCategories,
        pageNumber: nextPageNumber,
      }),
      replace: true,
    });
  };

  const selections: Record<FilterType, string[]> = {
    category: helpCategories,
    district: districts,
    state: states,
    county: counties,
    status: statuses,
  };

  const setSelections: Record<FilterType, (next: string[]) => void> = {
    category: setHelpCategories,
    district: setDistricts,
    state: setStates,
    county: setCounties,
    status: setStatuses,
  };

  const { toggleSelection, clearSelection } = createFilterSelectionActions<FilterType>({
    selections,
    setSelections,
    mapIncomingValue: (type, rawValue) => {
      if (type === 'district') return mapDistrictNameToCode(rawValue);
      if (type === 'category') return mapCategoryNameToCode(rawValue);
      return rawValue;
    },
    onBeforeMutate: () => setPageNumber(tableId, 1),
    onAfterMutate: (nextSelections) => {
      updateDashboardUrl({
        nextDistrictCodes: nextSelections.district,
        nextStates: nextSelections.state,
        nextCounties: nextSelections.county,
        nextStatuses: nextSelections.status,
        nextHelpCategories: nextSelections.category,
        nextPageNumber: 1,
      });
    },
  });

  const handleToggle = (e: ChangeEvent<HTMLInputElement>, type: FilterType) => {
    toggleSelection(type, e.target.value);
  };

  const helpCategoryItems = mapValueCountsToItems(data?.filters?.helpCategories, {
    getLabel: (value) => mapCategoryCodeToDisplay(value),
    getCheckedValue: (value) => value,
  });

  const districtItems = mapValueCountsToItems(data?.filters?.districts, {
    getCheckedValue: (value) => mapDistrictNameToCode(value),
  });

  const statusItems = mapValueCountsToItems(data?.filters?.statuses);
  const stateItems = mapValueCountsToItems(data?.filters?.states, {
    getLabel: (value) => (value === 'Undetermined' ? value : getStateNameFromCode(value)),
  });
  const countyItems = mapValueCountsToItems(data?.filters?.counties);

  const sections = [
    {
      id: 'help-categories',
      title: 'Inquiry Categories',
      type: 'category' as FilterType,
      selectedValues: helpCategories,
      items: helpCategoryItems,
      onToggle: handleToggle,
      onClear: () => clearSelection('category'),
      showClear: helpCategories.length > 0,
    },
    {
      id: 'districts',
      title: 'Districts',
      type: 'district' as FilterType,
      selectedValues: districts,
      items: districtItems,
      onToggle: handleToggle,
      onClear: () => clearSelection('district'),
      showClear: districts.length > 0,
    },
    {
      id: 'states',
      title: 'States',
      type: 'state' as FilterType,
      selectedValues: states,
      items: stateItems,
      onToggle: handleToggle,
      onClear: () => clearSelection('state'),
      showClear: states.length > 0,
    },
    {
      id: 'counties',
      title: 'Counties',
      type: 'county' as FilterType,
      selectedValues: counties,
      items: countyItems,
      onToggle: handleToggle,
      onClear: () => clearSelection('county'),
      showClear: counties.length > 0,
    },
  ];

  if (view !== 'unclaimed') {
    sections.push({
      id: 'statuses',
      title: 'Inquiry Status',
      type: 'status' as FilterType,
      selectedValues: statuses,
      items: statusItems,
      onToggle: handleToggle,
      onClear: () => clearSelection('status'),
      showClear: statuses.length > 0,
    });
  }

  return (
    <Filters<FilterType>
      panelTitle='Filter Inquiries'
      panelTitleClassName='table-filter-title-larger'
      loading={isLoading}
      sections={sections}
    />
  );
};

export default HelpRequestFilters;
